import _ from 'lodash'
import { isTag, type Document, type Element } from 'domhandler'
import type { ComponentExportStructure } from '@wix/document-services-types'
import type { TagRegistry } from '../../../tagRegistry'
import { WIX_INNER_ELEMENT_TAG } from '../constants'

export interface StructureCoreExport {
    readonly rootComponent: string
    readonly components: Record<string, ComponentExportStructure>
}

interface ExportNode {
    id?: string
    data?: Record<string, unknown>
    innerElements?: InnerElementsMap
    [key: string]: unknown
}

interface InnerElementsMap {
    [key: string]: ExportNode
}

interface ComponentConversionResult {
    readonly id: string
    readonly components: Record<string, ComponentExportStructure>
}

const parseDataProps = (propsValue: string | undefined, elementId: string): Record<string, unknown> | undefined => {
    if (propsValue === undefined) {
        return undefined
    }
    if (propsValue === '') {
        return {}
    }

    const parsedProps = _.attempt(JSON.parse, propsValue)
    if (_.isError(parsedProps)) {
        throw new Error(`Invalid data-props JSON on element "${elementId}": ${propsValue}`)
    }

    return parsedProps as Record<string, unknown>
}

const getChildElements = (element: Element): Element[] => element.children.filter(isTag)

const guardDuplicateId = (id: string, seenIds: Set<string>): void => {
    if (seenIds.has(id)) {
        throw new Error(`Duplicate id "${id}"`)
    }
    seenIds.add(id)
}

const guardDuplicateDataName = (name: string, innerElements: InnerElementsMap, parentId: string): void => {
    if (name in innerElements) {
        throw new Error(`Duplicate data-name "${name}" under parent "${parentId}"`)
    }
}

const buildInnerElementsMap = (
    entries: readonly (readonly [string, ExportNode])[],
    parentId: string,
): InnerElementsMap => {
    const map: InnerElementsMap = {}
    for (const [name, node] of entries) {
        guardDuplicateDataName(name, map, parentId)
        map[name] = node
    }
    return map
}

const convertInnerElement = (element: Element, seenIds: Set<string>, parentId: string): [string, ExportNode] => {
    const { id, 'data-name': name, 'data-props': dataProps } = element.attribs

    if (!name) {
        throw new Error(
            `Inner element <${WIX_INNER_ELEMENT_TAG}> missing required data-name attribute under "${parentId}"`,
        )
    }

    if (id) {
        guardDuplicateId(id, seenIds)
    }

    const parsedDataProps = parseDataProps(dataProps, name)
    const childElements = getChildElements(element)

    const innerEntries: [string, ExportNode][] = []
    _.forEach(childElements, child => {
        if (child.tagName !== WIX_INNER_ELEMENT_TAG) {
            throw new Error(`Unexpected non-inner-element tag <${child.tagName}> inside inner element "${name}"`)
        }
        innerEntries.push(convertInnerElement(child, seenIds, name))
    })
    const innerElements = buildInnerElementsMap(innerEntries, name)

    const node: ExportNode = {
        ...(id && { id }),
        ...(parsedDataProps !== undefined && { data: parsedDataProps }),
        ...(!_.isEmpty(innerElements) && { innerElements }),
    }

    return [name, node]
}

const convertComponentElement = (
    element: Element,
    registry: TagRegistry,
    seenIds: Set<string>,
): ComponentConversionResult => {
    const { id, 'data-type': type, 'data-props': dataProps } = element.attribs

    if (!id) {
        throw new Error(`Component element <${element.tagName}> missing required id attribute`)
    }
    guardDuplicateId(id, seenIds)

    const componentType = registry.getTypeByTag(element.tagName)
    if (!componentType) {
        throw new Error(`Unknown tag "${element.tagName}" not found in TagRegistry`)
    }
    registry.registerComponent(id, componentType)

    if (!type) {
        throw new Error(`Component element "${id}" missing required data-type attribute`)
    }

    const parsedDataProps = parseDataProps(dataProps, id)

    const childElements = getChildElements(element)
    const innerEntries: [string, ExportNode][] = []
    const childResults: ComponentConversionResult[] = []

    _.forEach(childElements, child => {
        if (child.tagName === WIX_INNER_ELEMENT_TAG) {
            innerEntries.push(convertInnerElement(child, seenIds, id))
        } else {
            childResults.push(convertComponentElement(child, registry, seenIds))
        }
    })

    const innerElements = buildInnerElementsMap(innerEntries, id)

    const component: ComponentExportStructure = {
        componentType,
        components: _.map(childResults, 'id'),
        type,
        ...(parsedDataProps !== undefined && { data: parsedDataProps }),
        ...(!_.isEmpty(innerElements) && { innerElements }),
    }

    const descendantComponents: Record<string, ComponentExportStructure> = {}
    _.forEach(childResults, result => {
        _.assign(descendantComponents, result.components)
    })

    return {
        id,
        components: { ...descendantComponents, [id]: component },
    }
}

export const convertStructureAstToCore = (doc: Document, registry: TagRegistry): StructureCoreExport => {
    const rootElements = doc.children.filter(isTag)
    if (rootElements.length === 0) {
        throw new Error('Document has no root element')
    }
    if (rootElements.length > 1) {
        throw new Error(`Document has multiple root elements, expected 1 but found ${rootElements.length}`)
    }

    const rootElement = rootElements[0] as Element
    const seenIds = new Set<string>()
    const { id, components } = convertComponentElement(rootElement, registry, seenIds)

    return {
        rootComponent: id,
        components,
    }
}
